import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import RcTooltip from 'rc-tooltip';
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded';
import VisibilityRounded from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRounded from '@mui/icons-material/VisibilityOffRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import SettingsSuggestRounded from '@mui/icons-material/SettingsSuggestRounded';
import CommonButton from '@src/new-components/Button';
import Typography from '@src/new-components/Typography';
import { COLOR } from '@src/styles/color';
import actions from '@src/redux/actions';
import i18n from 'i18next';
import CommonDialog from '@src/new-components/Dialog/CommonDialog';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { audioExpired, isBlockChanged } from '@src/services/project';
import { TRANSLATION_STATUS } from '@src/constants/project';
import { HISTORY_SCOPE } from '@src/constants/history';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import apis from '@src/apis';
import {
  StyledTranslationMenu,
  StyledTranslationDropdown,
  GlobalTooltipStyle,
} from './index.style';

const TranslationDropdown = ({
  onOpenTranslateOtherLanguage,
  projectId,
  // A download in flight: the trigger below is the only way into this menu, so
  // disabling it closes off translate / show-original / delete-translation at
  // once — all three change what the running export is built from.
  isLocked = false,
  // At least one block is being synthesized.
  hasSynthesizingBlock = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { language: currentLang } = i18n;
  const { getFeatureValue } = useFeatureFlags();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [loadingDeleteTranslation, setLoadingDeleteTranslation] =
    useState(false);
  const [openConfirmDeleteTranslation, setOpenConfirmDeleteTranslation] =
    useState(false);

  const {
    translation,
    showOriginalText,
    isProjectSynthesizing,
    isPreProcessingSynthesis,
    audioExpiredAt,
    audioLinkSrt,
    originalAudioType,
    audioType,
    multiBlock,
    isTranslationPendingSynthesis,
  } = useSelector((state) => state.project);
  const user = useSelector((state) => state.auth.user);
  const { languagesV3 } = useSelector((state) => state.language);
  const undoRedoEnabled = getFeatureValue(
    FEATURE_KEYS.PROJECT_DUBBING_UNDO_REDO,
    { userId: user?.id, email: user?.email, phoneNumber: user?.phoneNumber },
  );

  const isChangedAudioType = originalAudioType !== audioType;
  const hasAudioExpired = audioExpired(audioExpiredAt);
  const isTranslationSuccess =
    translation?.status === TRANSLATION_STATUS.SUCCESS;
  const { blocks, originalBlocks } = multiBlock;
  const blockWithText = Object.keys(blocks)
    .map((blockId) => blocks[blockId])
    .filter((block) =>
      isTranslationSuccess ? block?.translatedText : block?.text,
    );

  const blockWithTextChanged = useMemo(
    () =>
      blockWithText.filter((block) => {
        const currentBlock = {
          text: isTranslationSuccess ? block?.translatedText : block?.text,
          voiceCode: block?.voiceCode,
          speed: block?.speed,
          startTime: block?.startTime,
          endTime: block?.endTime,
          speakerId: block?.speakerId,
        };
        const originalBlock = {
          text: isTranslationSuccess
            ? originalBlocks[block.id]?.translatedText
            : originalBlocks[block.id]?.text,
          voiceCode: originalBlocks[block.id]?.voiceCode,
          speed: originalBlocks[block.id]?.speed,
          startTime: originalBlocks[block.id]?.startTime,
          endTime: originalBlocks[block.id]?.endTime,
          speakerId: originalBlocks[block.id]?.speakerId,
        };
        return isBlockChanged(currentBlock, originalBlock);
      }),
    [blockWithText, originalBlocks, audioType, isTranslationSuccess],
  );

  const hasAudio =
    !isChangedAudioType &&
    !!audioLinkSrt &&
    !!blockWithText.length &&
    !blockWithTextChanged?.length &&
    !hasAudioExpired &&
    !isTranslationPendingSynthesis;

  // A synthesis in flight is reading the text this menu would replace, so the
  // trigger itself is held, not the entries behind it. `isProjectSynthesizing`
  // does not see a per-block create-audio: that marks only the blocks, which is
  // what `hasSynthesizingBlock` reads.
  const isDisabledTranslateLanguage =
    isProjectSynthesizing ||
    isPreProcessingSynthesis ||
    isLocked ||
    hasSynthesizingBlock;

  const targetLanguageInfo = useMemo(() => {
    if (!translation?.targetLanguage) return null;
    return languagesV3.find(
      (lang) => lang.value === translation.targetLanguage,
    );
  }, [translation?.targetLanguage, languagesV3]);

  const handleTooltipVisibleChange = (visible) => {
    if (isDisabledTranslateLanguage) {
      setIsTooltipOpen(false);
      return;
    }
    setIsTooltipOpen(visible);
  };

  const handleTranslateOtherLanguage = () => {
    if (isDisabledTranslateLanguage) return;
    setIsTooltipOpen(false);
    onOpenTranslateOtherLanguage();
  };

  const handleDeleteTranslation = () => {
    if (isDisabledTranslateLanguage) return;
    setIsTooltipOpen(false);
    setOpenConfirmDeleteTranslation(true);
  };

  const handleToggleShowOriginal = () => {
    setIsTooltipOpen(false);
    dispatch(actions.project.toggleShowOriginalText());
  };

  const handleClearTranslation = async () => {
    try {
      setLoadingDeleteTranslation(true);
      // If any block has rewrite, delete project rewrite first
      const currentBlocks = multiBlock?.blocks || {};
      const hasRewriteInBlocks = Object.values(currentBlocks).some((block) =>
        Boolean(block?.rewrite),
      );
      if (hasRewriteInBlocks) {
        const deleteRewriteResponse = await apis.project.deleteProjectRewrite(
          projectId,
        );
        if (!deleteRewriteResponse?.status) {
          setLoadingDeleteTranslation(false);
          return;
        }
        const updatedBlocks = Object.entries(currentBlocks).reduce(
          (acc, [blockId, block]) => {
            acc[blockId] = block?.rewrite ? { ...block, rewrite: null } : block;
            return acc;
          },
          {},
        );
        dispatch(
          actions.project.setMultiBlock({
            ...multiBlock,
            blocks: updatedBlocks,
          }),
        );
      }

      const data = await apis.project.deleteTranslation(projectId);
      if (data?.status) {
        dispatch(
          actions.noti.push({
            severity: 'success',
            message: 'deleteTranslationSuccess',
          }),
        );
      }

      dispatch(actions.project.clearTranslation());
      if (undoRedoEnabled) {
        dispatch(actions.history.clear(HISTORY_SCOPE.DUBBING));
      }
    } catch (error) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: error?.response?.data?.error_message,
        }),
      );
    } finally {
      setIsTooltipOpen(false);
      setLoadingDeleteTranslation(false);
    }
  };

  const languageLabel = useMemo(() => {
    if (!targetLanguageInfo) return translation?.targetLanguage || '';

    // Try current language first (vi, en)
    if (targetLanguageInfo.label?.[currentLang]) {
      return targetLanguageInfo.label[currentLang];
    }

    // Fallback to Vietnamese
    if (targetLanguageInfo.label?.vi) {
      return targetLanguageInfo.label.vi;
    }

    // Fallback to English
    if (targetLanguageInfo.label?.en) {
      return targetLanguageInfo.label.en;
    }

    // Last resort: return the language code
    return translation?.targetLanguage || '';
  }, [targetLanguageInfo, currentLang, translation?.targetLanguage]);

  return (
    <StyledTranslationDropdown>
      <GlobalTooltipStyle />
      {isTooltipOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'transparent',
            zIndex: 9999,
            pointerEvents: 'auto',
          }}
          onWheel={(e) => e.preventDefault()}
          onTouchMove={(e) => e.preventDefault()}
        />
      )}
      {isTranslationSuccess && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Typography variant="body-14-medium" color={COLOR.label2[64]}>
            {`${t('translation')}:`}
          </Typography>
          <RcTooltip
            placement="bottomRight"
            overlay={
              <StyledTranslationMenu>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={handleTranslateOtherLanguage}
                  aria-disabled={isDisabledTranslateLanguage}
                  style={{
                    opacity: isDisabledTranslateLanguage ? 0.5 : 1,
                    cursor: isDisabledTranslateLanguage
                      ? 'not-allowed'
                      : 'pointer',
                  }}
                  className="item"
                >
                  <TranslateRoundedIcon
                    sx={{ width: 20, height: 20, color: COLOR.darkGray }}
                  />
                  <Typography variant="body-14-medium" color={COLOR.darkGray}>
                    {t('translateToOtherLanguage')}
                  </Typography>
                </div>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={handleToggleShowOriginal}
                  className="item"
                >
                  {showOriginalText ? (
                    <VisibilityOffRounded
                      sx={{ width: 20, height: 20, color: COLOR.darkGray }}
                    />
                  ) : (
                    <VisibilityRounded
                      sx={{ width: 20, height: 20, color: COLOR.darkGray }}
                    />
                  )}
                  <Typography variant="body-14-medium" color={COLOR.darkGray}>
                    {showOriginalText ? t('hideOriginal') : t('showOriginal')}
                  </Typography>
                </div>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={handleDeleteTranslation}
                  aria-disabled={isDisabledTranslateLanguage}
                  style={{
                    opacity: isDisabledTranslateLanguage ? 0.5 : 1,
                    cursor: isDisabledTranslateLanguage
                      ? 'not-allowed'
                      : 'pointer',
                  }}
                  className="item"
                >
                  <DeleteOutlineRounded
                    sx={{ width: 20, height: 20, color: COLOR.darkGray }}
                  />
                  <Typography variant="body-14-medium" color={COLOR.darkGray}>
                    {t('deleteTranslation')}
                  </Typography>
                </div>
              </StyledTranslationMenu>
            }
            trigger={['click']}
            visible={isTooltipOpen}
            onVisibleChange={handleTooltipVisibleChange}
            showArrow={false}
            align={{
              offset: [0, 5],
            }}
            zIndex={10000}
          >
            <div>
              <CommonButton
                variant="outline"
                disabled={isDisabledTranslateLanguage}
                customStyle={{
                  padding: '6px 12px',
                  borderRadius: '8px',
                  border: `1px solid ${COLOR.indigo[16]}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  '&:hover': {
                    backgroundColor: COLOR.indigo[8],
                  },
                  '&:disabled': {
                    pointerEvents: 'all',
                    cursor: 'not-allowed',
                    opacity: 0.5,
                    '&:hover': {
                      backgroundColor: COLOR.white,
                      border: `1px solid ${COLOR.secondary['32']}`,
                    },
                  },
                }}
              >
                <Typography
                  variant="body-14-medium"
                  color={COLOR.indigo.default}
                >
                  {languageLabel}
                </Typography>
                <SettingsSuggestRounded
                  sx={{ width: 20, height: 20, color: COLOR.indigo.default }}
                />
              </CommonButton>
            </div>
          </RcTooltip>
        </div>
      )}
      <CommonDialog
        width="512px"
        title={t('confirmToDeleteTheTranslation')}
        fullWidth
        contentComponent={
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: hasAudio
                ? t('deleteTranslationHasAudio')
                : t('descriptionConfirmToDeleteTheTranslation'),
            }}
          />
        }
        open={openConfirmDeleteTranslation}
        onClose={() => {
          if (loadingDeleteTranslation) return;
          setOpenConfirmDeleteTranslation(false);
        }}
        actionComponent={
          <>
            <CommonButton
              customStyle={{
                padding: '8px 24px',
                borderRadius: '8px',
              }}
              variant="outlineSmall"
              textProps={{
                variantText: 'body-14-semibold',
                color: COLOR.darkBlue,
              }}
              onClick={() => {
                if (loadingDeleteTranslation) return;
                setOpenConfirmDeleteTranslation(false);
              }}
            >
              {t('cancel')}
            </CommonButton>
            <CommonButton
              customStyle={{
                padding: '8px 24px',
                backgroundColor: COLOR.error,
                borderRadius: '8px',
                '&:disabled': {
                  backgroundColor: COLOR.error,
                  opacity: 0.5,
                  cursor: 'not-allowed',
                  pointerEvents: 'auto',
                },
              }}
              textProps={{
                variantText: 'body-14-semibold',
                color: COLOR.white,
              }}
              disabled={loadingDeleteTranslation}
              color="error"
              variant="contained"
              onClick={handleClearTranslation}
            >
              {t('delete')}
            </CommonButton>
          </>
        }
      />
    </StyledTranslationDropdown>
  );
};

export default TranslationDropdown;
